<script setup>
import ChallengeProvider from '/app/components/providers/ChallengeProvider.vue';
import { get } from 'lodash';
import { isAMA } from '/app/utils/challenge';
import { api } from '/app/plugins/services';
import { sentry, captureRedirectError } from '/app/plugins/sentry';
import { isDifferentOrigin, surveyModeRedirection } from '/app/utils/route';
import { useDetails } from '/app/composables/challenge';
import { Session } from '/app/utils/auth';
import { useFeatureFlag } from '/app/composables/feature-flag';
import { shallowRef, computed, onMounted, onUpdated } from 'vue';
import { useHead } from '@unhead/vue';
import { useRoute, useRouter } from 'vue-router';
import { useAppState } from '/app/composables/app-state';
import { config } from '/app/config';
import { redirectTo, currentOrigin } from '/app/utils/navigation';

const accountURL = config.account.url;

const route = useRoute();
const router = useRouter();
const { fetchUser } = useAppState();
const { variantTo } = useFeatureFlag('survey-mode-link');
const challenge = shallowRef(null);
const details = useDetails(challenge);

useHead(computed(() => ({
    title: challenge.value ? details.questionTitle.value : 'Challenge',
})));

function isEnabledFor(userId) {
    const variant = variantTo(userId);
    return get(variant.value, 'payload.auto_survey');
}

function fetchCurrentUser() {
    if (challenge.value && challenge.value.catalyst) {
        fetchUser(challenge.value.catalyst.handle).catch(() => {
            sentry.addBreadcrumb({
                category: 'Challenge',
                message: 'Error fetching current user',
                level: 'error'
            });
        });
    }
}

const fetchChallenge = async () => {
    try {
        const { data: response } = await api.challenges.get(route.params.challenge);
        const { data: challengeData } = response;

        if (isAMA(challengeData)) {
            router.replace({
                name: 'suggestion-box.ama',
                params: { platformSlug: challengeData.catalyst.handle },
            });
            return;
        }

        if (challengeData.is_closed && route.name !== 'challenges.show') {
            console.log('[ChallengeWrapper] challenge is closed, redirecting to show')
            router.replace({
                name: 'challenges.show',
                params: { challenge: route.params.challenge },
            });
            return;
        }

        challenge.value = challengeData;
    } catch (err) {
        if (err.response && err.response.status === 401) {
            const continueURL = encodeURIComponent(`${currentOrigin()}${route.fullPath}`);
            redirectTo(`${accountURL}/gateway?continue=${continueURL}`);
            return;
        }

        captureRedirectError('ChallengeWrapper', err, {
            challenge: route.params.challenge,
            route: route.fullPath,
            routeName: route.name,
        });

        let feedback = `\n\nchallenge: ${route.params.challenge};\n`;
        const errors = get(err, 'response.data.errors', []);

        if (errors.length) {
            feedback += `${errors.map(c => c.error).join(';\n')}`;
        }

        router.replace({
            name: 'send-feedback',
            query: { about: 'Challenge - Rating Flow', feedback }
        });
    }
};

fetchChallenge();

onMounted(() => {
    const hasBack = get(router, 'options.history.state.back', false);

    const canRedirect = !Session.exists()
        && isDifferentOrigin()
        && !hasBack
        && !details.isAMA.value
        && !challenge.value?.is_closed
        && !challenge.value?.idea_entry_fee;

    if (canRedirect) {
        const userId = get(challenge.value, 'catalyst.id', 'none');

        if (isEnabledFor(userId)) {
            surveyModeRedirection(route);
        }
    }
});

onUpdated(() => {
    fetchCurrentUser();
});
</script>

<template>
    <ChallengeProvider :challenge="challenge">
        <RouterView v-if="challenge" :challenge="challenge" />

        <div v-else class="home-grid challenge-view pt-3 animate-pulse">
            <div class="home-grid__top-content space-y-6 mb-4">
                <!-- Breadcrumbs -->
                <div class="flex gap-2 items-center">
                    <div class="h-4 bg-dust-200 rounded w-16"></div>
                    <div class="h-4 bg-dust-200 rounded w-4"></div>
                    <div class="h-4 bg-dust-200 rounded w-24"></div>
                    <div class="h-4 bg-dust-200 rounded w-4"></div>
                    <div class="h-4 bg-dust-300 rounded w-48"></div>
                </div>

                <!-- Challenge detail card -->
                <div class="challenge-detail-card">
                    <div class="flex gap-4 p-4">
                        <div class="w-48 h-36 bg-dust-300 rounded shrink-0"></div>
                        <div class="flex-1 space-y-3 pt-1">
                            <div class="h-6 bg-dust-300 rounded w-5/6"></div>
                            <div class="flex items-center gap-2">
                                <div class="w-8 h-8 bg-dust-300 rounded-full"></div>
                                <div class="h-4 bg-dust-200 rounded w-24"></div>
                            </div>
                            <div class="space-y-2 pt-2">
                                <div class="h-3 bg-dust-200 rounded w-full"></div>
                                <div class="h-3 bg-dust-200 rounded w-full"></div>
                                <div class="h-3 bg-dust-200 rounded w-4/5"></div>
                                <div class="h-3 bg-dust-200 rounded w-3/5"></div>
                            </div>
                        </div>
                    </div>
                    <!-- Stats bar -->
                    <div class="flex gap-4 px-4 py-3 border-t border-dust-200">
                        <div class="h-4 bg-dust-200 rounded w-16"></div>
                        <div class="h-4 bg-dust-200 rounded w-12"></div>
                        <div class="h-4 bg-dust-200 rounded w-16"></div>
                        <div class="h-4 bg-dust-200 rounded w-14"></div>
                        <div class="ml-auto h-4 bg-dust-200 rounded w-40"></div>
                    </div>
                </div>

                <!-- Tabs -->
                <div class="flex gap-6 border-b border-dust-200 pb-3">
                    <div class="h-5 bg-dust-300 rounded w-28"></div>
                    <div class="h-5 bg-dust-200 rounded w-28"></div>
                </div>

                <!-- Tab content area -->
                <div class="space-y-4">
                    <div class="h-4 bg-dust-200 rounded w-48"></div>
                    <div class="h-32 bg-dust-100 rounded border border-dust-200"></div>
                    <div class="space-y-2">
                        <div class="h-3 bg-dust-200 rounded w-full"></div>
                        <div class="h-3 bg-dust-200 rounded w-2/3"></div>
                    </div>
                </div>

                <!-- Rate button -->
                <div class="flex justify-center">
                    <div class="h-14 bg-dust-300 rounded-md w-[17.5rem]"></div>
                </div>
            </div>

            <div class="home-grid__sidebar flex flex-col gap-6">
                <!-- Challenge Rating card -->
                <div class="border border-dust-200 rounded-md">
                    <div class="h-10 bg-dust-400 rounded-t-md"></div>
                    <div class="p-6 space-y-3 flex flex-col items-center">
                        <div class="h-4 bg-dust-200 rounded w-32"></div>
                        <div class="flex gap-1">
                            <div class="w-7 h-7 bg-dust-200 rounded" v-for="n in 5" :key="n"></div>
                        </div>
                        <div class="h-9 bg-dust-300 rounded w-36"></div>
                    </div>
                </div>

                <!-- Tournaments widget -->
                <div class="border border-dust-200 rounded-md">
                    <div class="h-10 bg-dust-400 rounded-t-md"></div>
                    <div class="p-4 space-y-3">
                        <div class="flex items-center gap-2" v-for="n in 4" :key="n">
                            <div class="w-5 h-4 bg-dust-200 rounded"></div>
                            <div class="w-8 h-8 bg-dust-200 rounded-full"></div>
                            <div class="h-4 bg-dust-200 rounded w-24"></div>
                            <div class="ml-auto h-4 bg-dust-300 rounded w-10"></div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </ChallengeProvider>
</template>
